import React, { useState, useEffect } from 'react';
import { FiHome, FiBook, FiVideo, FiUsers, FiUpload, FiFile, FiPlay, FiEdit, FiTrash2, FiPlus } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { showToast } from '../../components/common/Toaster';
import { 
  DashboardContainer, 
  DashboardHeader, 
  SectionTitle,
  ContentGrid,
  ContentCard,
  ContentCardHeader,
  ContentCardBody,
  ContentCardFooter,
  ContentActions,
  ActionButton,
  EmptyState,
} from '../dashboards/Dashboard.styles';

const TeacherContent = () => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState('video');

  const menuItems = [
    { icon: <FiHome />, label: 'Dashboard', path: '/teacher' },
    { icon: <FiVideo />, label: 'Content Library', path: '/teacher/content' },
    { icon: <FiUsers />, label: 'Grades', path: '/teacher/grades' },
  ];

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      // For now, show empty state - would fetch from API
      setContent([]);
    } catch (error) {
      showToast('error', 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'video':
        return <FiPlay />;
      case 'file':
        return <FiFile />;
      case 'quiz':
        return <FiBook />;
      default:
        return <FiUpload />;
    }
  };

  const getContentColor = (type) => {
    switch (type) {
      case 'video':
        return '#EF4444';
      case 'file':
        return '#3B82F6';
      case 'quiz':
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  const handleUploadContent = () => {
    setShowUploadModal(true);
  };

  const handleDeleteContent = (contentId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    
    try {
      // API call to delete content would go here
      showToast('success', 'Content deleted successfully');
      fetchContent();
    } catch (error) {
      showToast('error', 'Failed to delete content');
    }
  };

  const handleEditContent = (contentId) => {
    // Navigate to edit content page or open edit modal
    showToast('info', 'Edit functionality coming soon');
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <div>Loading content...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems}>
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>Content Library 📚</h1>
            <p>Manage your videos, files, and quizzes</p>
          </div>
          <Button
            leftIcon={<FiPlus />}
            onClick={handleUploadContent}
          >
            Upload Content
          </Button>
        </DashboardHeader>

        <SectionTitle>All Content</SectionTitle>

        {content.length === 0 ? (
          <EmptyState>
            <FiUpload size={48} />
            <h3>No content yet</h3>
            <p>Upload your first video, file, or create a quiz to get started</p>
            <Button leftIcon={<FiPlus />} onClick={handleUploadContent}>
              Upload Content
            </Button>
          </EmptyState>
        ) : (
          <ContentGrid>
            {content.map((item) => (
              <ContentCard key={item.id}>
                <ContentCardHeader>
                  <div>
                    <h3>{item.title}</h3>
                    <span className="badge" style={{ 
                      background: getContentColor(item.type) + '20',
                      color: getContentColor(item.type)
                    }}>
                      {item.type.toUpperCase()}
                    </span>
                  </div>
                </ContentCardHeader>
                
                <ContentCardBody>
                  <p><strong>Course:</strong> {item.course}</p>
                  <div className="meta">
                    {item.duration && <span>⏱️ {item.duration}</span>}
                    {item.size && <span>📦 {item.size}</span>}
                    {item.questions && <span>❓ {item.questions} questions</span>}
                    <span>📊 {item.status}</span>
                  </div>
                </ContentCardBody>
                
                <ContentCardFooter>
                  <ContentActions>
                    <ActionButton title="View Content">
                      <FiPlay />
                    </ActionButton>
                    <ActionButton title="Edit Content" onClick={() => handleEditContent(item.id)}>
                      <FiEdit />
                    </ActionButton>
                    <ActionButton title="Delete Content" onClick={() => handleDeleteContent(item.id, item.title)}>
                      <FiTrash2 />
                    </ActionButton>
                  </ContentActions>
                </ContentCardFooter>
              </ContentCard>
            ))}
          </ContentGrid>
        )}

        {/* Upload Content Modal */}
        <Modal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          title="Upload Content"
          size="medium"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Content Type
              </label>
              <select
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #E5E7EB',
                  borderRadius: '12px',
                  fontSize: '16px',
                }}
              >
                <option value="video">Video</option>
                <option value="file">File</option>
                <option value="quiz">Quiz</option>
              </select>
            </div>

            <div style={{ 
              padding: '40px', 
              border: '2px dashed #E5E7EB', 
              borderRadius: '12px', 
              textAlign: 'center',
              background: '#F9FAFB'
            }}>
              <FiUpload size={48} style={{ color: '#6B7280', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>
                {uploadType === 'video' && 'Upload Video'}
                {uploadType === 'file' && 'Upload File'}
                {uploadType === 'quiz' && 'Create Quiz'}
              </h3>
              <p style={{ margin: '0 0 16px 0', color: '#6B7280' }}>
                {uploadType === 'video' && 'Drag and drop your video file here, or click to browse'}
                {uploadType === 'file' && 'Drag and drop your file here, or click to browse'}
                {uploadType === 'quiz' && 'Create a new quiz for your course'}
              </p>
              <Button variant="outline">
                {uploadType === 'quiz' ? 'Create Quiz' : 'Choose File'}
              </Button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
              <Button variant="ghost" onClick={() => setShowUploadModal(false)}>
                Cancel
              </Button>
              <Button onClick={() => {
                showToast('info', 'Upload functionality coming soon!');
                setShowUploadModal(false);
              }}>
                {uploadType === 'quiz' ? 'Create Quiz' : 'Upload'}
              </Button>
            </div>
          </div>
        </Modal>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default TeacherContent;
